// React & State Management
import { memo, useCallback, useEffect, useState } from "react";

// Types
import { PriceListEditProps } from "./types";
import {
  PriceListEditFormValues,
  PriceTable,
  RentDuration,
} from "services/api/prices/types";
import { VehicleGroupVehicleRow } from "services/api/calculations/vehiclegroup/types";

// API & Constants
import {
  createPriceListAPI,
  getPriceListAPI,
  getPriceTableAPI,
  updatePriceListAPI,
} from "services/api/prices/api";
import { getVehicleGroupVehiclesAPI } from "services/api/calculations/vehiclegroup/api";
import {
  initialPriceListEditForm,
  initialPriceTable,
} from "services/api/prices/consts";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";

// UI Components
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { Loader } from "ui/components/Loader";
import VehicleGroupDropdown from "ui/components/Dropdown/VehicleGroupDropdown/VehicleGroupDropdown";

// Helpers
import { classNames, useTranslate } from "helpers";

// Forms & Validation
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";
import {
  useHandleDropdownChange,
  useHandleTextFieldChange,
} from "services/forms/hooks";

// Form Schema
import { PRICE_LIST_EDIT_FORM_SCHEMA } from "./validation";

const PriceListEditCard: React.FC<PriceListEditProps> = (props) => {
  const { priceListId, onSuccess } = props;
  const { t } = useTranslate();

  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [formValues, setFormValues] = useState<PriceListEditFormValues>(
    initialPriceListEditForm
  );

  const [vehicles, setVehicles] = useState<
    DataWithPagination<VehicleGroupVehicleRow>
  >(initialDataWithPagination);

  const [priceTable, setPriceTable] = useState<PriceTable>(initialPriceTable);

  useEffect(() => {
    if (priceListId) {
      fetchPriceList(priceListId);
      fetchPriceTable(priceListId);
    }
  }, [priceListId]);

  useEffect(() => {
    if (formValues.vehicleGroupId) {
      fetchVehicleGroupVehicles(formValues.vehicleGroupId, vehicles.pagination);
    }
  }, [formValues.vehicleGroupId]);

  const fetchPriceList = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialPriceListEditForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getPriceListAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);

    setIsLoading(false);

    setErrors({});
  };

  const fetchPriceTable = async (id: string) => {
    if (id === undefined) {
      setPriceTable(initialPriceTable);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getPriceTableAPI(id);
      if (result) setPriceTable(result);
    } catch {
      setIsLoading(false);
      return;
    }

    setPriceTable(result);

    setIsLoading(false);

    setErrors({});
  };

  const fetchVehicleGroupVehicles = async (
    vehicleGroupId: string,
    pagination: Pagination
  ) => {
    setIsLoading(true);
    try {
      const results = await getVehicleGroupVehiclesAPI(
        pagination,
        "",
        vehicleGroupId
      );

      setVehicles(results);
    } catch {
      setVehicles(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(
      formValues,
      PRICE_LIST_EDIT_FORM_SCHEMA,
      setErrors
    );

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    try {
      let result;
      let id = !!priceListId?.trim()
        ? priceListId
        : !!formValues.id?.trim()
        ? formValues.id
        : null;

      if (!!id) {
        result = await updatePriceListAPI(id, formValues);
      } else {
        result = await createPriceListAPI(formValues);
      }

      if (result && onSuccess) {
        if (formValues.id) {
          onSuccess();
        } else {
          setFormValues(result);
          fetchPriceTable(result.id);
        }
      }
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  const handleDropdownChange = useHandleDropdownChange(
    setFormValues,
    formValues
  );

  // const priceListResults: PriceListResult[] = [
  //   {
  //     vehicle: {
  //       id: "1",
  //       name: "Opel Corsa",
  //     },
  //     priceData: {
  //       "1-2 dana": 30,
  //       "3-6 dana": 28,
  //       "7-23 dana": 25,
  //       "1 mj": 350,
  //       "6 mj": 235,
  //       "12 mj": 720,
  //     },
  //   },
  //   {
  //     vehicle: {
  //       id: "1",
  //       name: "VW Golf",
  //     },
  //     priceData: {
  //       "1-2 dana": 40,
  //       "3-6 dana": 37,
  //       "7-23 dana": 32,
  //       "1 mj": 600,
  //       "6 mj": 515,
  //       "12 mj": 655,
  //     },
  //   },
  //   {
  //     vehicle: {
  //       id: "3",
  //       name: "E-Klasa",
  //     },
  //     priceData: {
  //       "1-2 dana": 90,
  //       "3-6 dana": 82,
  //       "7-23 dana": 75,
  //       "1 mj": 1200,
  //       "6 mj": 1600,
  //       "12 mj": 1700,
  //     },
  //   },
  // ];

  const handlePriceChange = useCallback(
    (e: any, rangeId: string, vehicleId: string) => {
      if (formValues.id) {
        // const model = {
        //   residualValueKmId: kmRangeId,
        //   residualValueMonthId: monthId,
        //   percentage: parseFloat(e.target.value),
        // } as ResidualValueTableEditFormValues;
        // await updateResidualValueTableAPI(formValues.id, model);
      }
    },
    [formValues.id]
  );

  const rentDurationHeader = (range: RentDuration) => {
    if (!range) return "";

    let value;

    if (range.start === range.end) {
      value = range.start;
    } else {
      value = `${range.start}-${range.end}`;
    }

    return `${value} ${range.startRentDurationUnit.name}`;
  };

  const renderPercentageTextbox = (
    rangeId: string,
    vehicleId: string
    // values: ResidualValueTableValue[]
  ) => {
    let valuePercentage = 0;
    // let valuePercentage = transformResidualValueTableValue(
    //   kmId,
    //   monthId,
    //   values
    // );

    return (
      <TextField
        type="number"
        name="value"
        onChange={(e: any) => handlePriceChange(e, rangeId, vehicleId)}
        value={valuePercentage}
        debounceTime={1000}
        required
        groupAppend="%"
        cssClassName={classNames(
          "!ring-0 !rounded-none",
          valuePercentage > 0 ? "font-bold !text-red-500" : ""
        )}
      ></TextField>
    );
  };


  return (
    <>
      <div className="flex gap-4">
        <div className="w-full m-auto">
          <div className="space-y-3">
            <form noValidate className="text-left">
              <div className="px-5 pb-3 mb-2">
                <div className="flex gap-3 mb-3">
                  <div className="flex-1 ">
                    <TextField
                      type="text"
                      name="name"
                      label={t("admin.pricelist.add.name")}
                      placeholder={t(
                        "admin.pricelist.add.name.placeholder"
                      )}
                      onChange={handleTextFieldChange}
                      error={errors.name}
                      value={formValues.name}
                      required
                      endIcon=""
                    ></TextField>
                  </div>
                </div>
                <div className="flex gap-3 mb-3">
                  <div className="flex-1 ">
                    <VehicleGroupDropdown
                      label={t("admin.pricelist.add.vehiclegroup")}
                      placeholder={t("admin.vehicle.group.placeholder")}
                      name="vehicleGroupId"
                      value={formValues.vehicleGroupId}
                      onChange={handleDropdownChange}
                      error={errors.vehicleGroupId}
                      required={true}
                    />
                  </div>
                </div>
              </div>
              {formValues.id && (
                <div className="px-0 pt-2 mx-[-1px] xl:px-6 mb-6">
                  <table className="table-auto border-collapse border border-slate-300 w-full">
                    <thead>
                      <tr>
                        <th className="border border-slate-300 p-2 bg-slate-100 font-bold text-center text-sm  text-slate-900 uppercase min-w-[200px]">
                          Model
                        </th>
                        {priceTable.rentDurations.map(
                          (range: RentDuration, index: number) => (
                            <th
                              key={index}
                              className="border border-slate-300 p-2 bg-slate-50  px-4 py-3 text-xs font-normal text-center text-slate-900 uppercase"
                            >
                              {rentDurationHeader(range)}
                            </th>
                          )
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {vehicles.data.map(
                        (vehicle: VehicleGroupVehicleRow, index: number) => (
                          <tr key={index}>
                            <td className="border border-slate-300 bg-slate-50 text-left px-4 py-1 text-sm font-medium ">
                              <div className="flex gap-2">
                                <div className="flex items-center">
                                  <img
                                    src={vehicle.model.image ?? ""}
                                    alt=""
                                    className="w-8"
                                  />
                                </div>
                                <div>
                                  <div className="text-xs">
                                    {vehicle.manufacturer.name}{" "}
                                    {vehicle.model.name} {vehicle.type.name}{" "}
                                  </div>
                                  <div className="text-xs text-slate-600">
                                    <span>GODINA:{vehicle.model.year} </span>
                                    <span>
                                      KW/HP:
                                      {vehicle.type.maximumPowerKw}/
                                      {vehicle.type.maximumPowerHp}{" "}
                                    </span>
                                    {/* <span>GORIVO:{vehicle.type.fuelType?.name} </span> */}
                                  </div>
                                </div>
                              </div>
                            </td>
                            {priceTable.rentDurations.map(
                              (range: RentDuration, index: number) => (
                                <td
                                  key={index}
                                  className="border border-slate-300  bg-white text-center  text-sm font-medium"
                                >
                                  {range.id && vehicle.id && (
                                    <>
                                      {renderPercentageTextbox(
                                        range.id,
                                        vehicle.id
                                      )}
                                    </>
                                  )}
                                </td>
                              )
                            )}
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="px-5 pb-5 flex justify-end ">
                <Button
                  variant="primary"
                  size="full"
                  children={t("admin.save")}
                  onClick={handleFormSubmit}
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );
};
export default memo(PriceListEditCard);
